/**
 * Tipos compartidos del módulo de marketing.
 *
 * La fuente de verdad de los planes es el backend (apps/api/servers.json),
 * expuesto vía GET /api/servers/plans/:game. Estos tipos reflejan la
 * respuesta exacta del endpoint para mantener sincronía.
 */

export interface Plan {
  id: string
  label: string
  description: string
  binary: string
  /** Código de ubicación Hetzner: ash, nbg1, fsn1, hel1, hil */
  location: string
  /** Precio en USD por hora */
  price: number
  /** RAM en GB (opcional, viene en description) */
  ram?: number
}

export interface PlansResponse {
  game: string
  plans: Plan[]
}

/**
 * Parsea el campo `description` del plan para extraer specs separadas.
 * Formato esperado: "XGB RAM | Y vCPUs | ZGB SSD"
 */
export function parsePlanSpecs(description: string): {
  ram: string
  vcpu: string
  ssd: string
} {
  const ram = description.match(/(\d+)\s*GB\s*RAM/i)?.[1]
  const vcpu = description.match(/(\d+)\s*vCPUs?/i)?.[1]
  const ssd = description.match(/(\d+)\s*GB\s*SSD/i)?.[1]
  return {
    ram: ram ? `${ram} GB` : '?',
    vcpu: vcpu ? `${vcpu} vCPUs` : '?',
    ssd: ssd ? `${ssd} GB SSD` : '?',
  }
}

interface LocationInfo {
  region: string
  /** Código de país ISO 3166-1 alpha-2 (US, DE, FI…) para `react-world-flags`. */
  countryCode: string
  location: string
}

/**
 * Mapa de códigos Hetzner → datos legibles.
 * Mantener en sync con LOCATION_FLAGS en ServerPanel.tsx.
 */
const LOCATION_MAP: Record<string, LocationInfo> = {
  ash:  { region: 'EEUU',      countryCode: 'US', location: 'Ashburn, VA' },
  hil:  { region: 'EEUU',      countryCode: 'US', location: 'Hillsboro, OR' },
  nbg1: { region: 'Alemania',  countryCode: 'DE', location: 'Nuremberg' },
  fsn1: { region: 'Alemania',  countryCode: 'DE', location: 'Falkenstein' },
  hel1: { region: 'Finlandia', countryCode: 'FI', location: 'Helsinki' },
}

/** Mapea código de ubicación del backend a info legible. */
export function locationInfo(code: string): LocationInfo {
  return (
    LOCATION_MAP[code.toLowerCase()] ?? {
      region: code,
      countryCode: 'XX',
      location: code,
    }
  )
}